/**
 * CustomDataGridMobile Component
 *
 * Mobile-optimized card-based list view. Features:
 * - Card layout with title, subtitle, and detail fields
 * - Expandable cards (when expand prop is provided)
 * - Responsive detail grid (2 or 3 columns)
 * - Touch-friendly action buttons
 *
 * This component automatically extracts fields from children and
 * organizes them into a card layout based on primarySource and secondarySource props.
 */

import React, { useState } from 'react';
import {
  useListContext,
  DatagridProps,
  RaRecord,
  RecordContextProvider,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  Grid,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

interface CustomDataGridMobileProps<RecordType extends RaRecord = any>
  extends Omit<DatagridProps<RecordType>, 'children'> {
  children: React.ReactNode;
  primarySource?: string;
  secondarySource?: string;
  detailColumns?: 2 | 3;
}

/**
 * Individual expandable card component
 */
const ExpandableCard = <RecordType extends RaRecord = any>({
  record,
  index,
  actions,
  titleEl,
  subtitleEl,
  others,
  detailColumns,
  ExpandComponent,
  theme,
  isXs,
  translate,
}: {
  record: RecordType;
  index: number;
  actions: React.ReactElement[];
  titleEl: React.ReactElement | null;
  subtitleEl: React.ReactElement | null;
  others: React.ReactElement[];
  detailColumns: 2 | 3;
  ExpandComponent?: any;
  theme: any;
  isXs: boolean;
  translate: (key: string) => string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isExpandable = !!ExpandComponent;

  const handleExpandClick = () => {
    if (isExpandable) {
      setExpanded(!expanded);
    }
  };

  return (
    <Card
      key={(record as any).id ?? index}
      onClick={isExpandable ? handleExpandClick : undefined}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: 0,
        transition: 'box-shadow .2s ease, border-color .2s ease, transform .1s ease',
        cursor: isExpandable ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: 2,
          borderColor: theme.palette.primary.light,
          ...(isExpandable && { transform: 'translateY(-2px)' }),
        },
        ...(expanded && {
          borderColor: theme.palette.primary.main,
          boxShadow: 3,
        }),
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        {/* Header: title + expand icon or actions. Wraps on xs so actions drop below the title. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
            {/* Title */}
            <Box
              sx={{
                '& .MuiTypography-root': {
                  fontSize: { xs: '1.06rem', sm: '1.12rem' },
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                },
              }}
            >
              {titleEl && (
                <RecordContextProvider value={record}>
                  {React.cloneElement(titleEl as any, {
                    record,
                    sx: { fontWeight: 700, color: 'text.primary', lineHeight: 1.3 },
                  })}
                </RecordContextProvider>
              )}
            </Box>
            {/* Subtitle */}
            {subtitleEl && (
              <Box
                sx={{
                  mt: 0.35,
                  '& .MuiTypography-root': {
                    fontSize: { xs: '.92rem', sm: '.96rem' },
                    color: 'text.secondary',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  },
                }}
              >
                <RecordContextProvider value={record}>
                  {React.cloneElement(subtitleEl as any, { record })}
                </RecordContextProvider>
              </Box>
            )}
          </Box>

          {/* Expand icon or Actions */}
          {isExpandable ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleExpandClick();
              }}
              aria-expanded={expanded}
              aria-label="show more"
              size="small"
              sx={{
                flexShrink: 0,
                alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                color: expanded ? 'primary.main' : 'action.active',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          ) : (
            actions.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexShrink: 0,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                <RecordContextProvider value={record}>
                  {actions.map((a, i) =>
                    React.cloneElement(a as any, {
                      key: i,
                      record,
                      size: isXs ? 'small' : 'medium',
                    })
                  )}
                </RecordContextProvider>
              </Box>
            )
          )}
        </Box>

        {/* Divider */}
        <Divider sx={{ my: { xs: 0.9, sm: 1.1 }, opacity: 0.55 }} />

        {/* Details grid */}
        <Grid container spacing={{ xs: 1, sm: 1.5 }} columns={{ xs: 1, sm: detailColumns }}>
          {others.map((field, i) => {
            const label = field.props?.label ?? field.props?.source;

            return (
              <Grid item xs={1} sm={1} key={label ?? i}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    columnGap: 0.8,
                    rowGap: 0.25,
                    minHeight: 30,
                    py: 0.25,
                  }}
                >
                  {/* Label */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.primary.main,
                      lineHeight: 1.35,
                    }}
                  >
                    {translate(label)}
                  </Typography>

                  {/* Value */}
                  <Box
                    sx={{
                      flex: '1 1 180px',
                      minWidth: 0,
                      '& .MuiTypography-root': {
                        fontSize: theme.typography.body2.fontSize,
                        fontWeight: 500,
                        color: 'text.primary',
                      },
                      '& .MuiChip-root': {
                        height: 24,
                        fontSize: theme.typography.body2.fontSize as any,
                      },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <RecordContextProvider value={record}>
                      {React.cloneElement(field as any, { record })}
                    </RecordContextProvider>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Expanded content */}
        {ExpandComponent && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <RecordContextProvider value={record}>
                {typeof ExpandComponent === 'function' ? (
                  <ExpandComponent id={(record as any).id} record={record} resource="" />
                ) : (
                  ExpandComponent
                )}
              </RecordContextProvider>
            </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Main mobile data grid component
 */
const CustomDataGridMobile = <RecordType extends RaRecord = any>({
  children,
  primarySource,
  secondarySource,
  detailColumns = 2,
  expand,
}: CustomDataGridMobileProps<RecordType>) => {
  const { data = [], isLoading } = useListContext();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const translate = useTranslate();

  /** Split children into title, subtitle, detail fields and actions */
  const splitChildren = (_record: RecordType) => {
    const fields: Array<React.ReactElement & { props: any }> = [];
    const actions: Array<React.ReactElement & { props: any }> = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;
      const c = child as React.ReactElement & { props: any };
      const { source, label } = c.props || {};
      if (!source && !label) actions.push(c);
      else fields.push(c);
    });

    let titleEl: React.ReactElement | null = null;
    let subtitleEl: React.ReactElement | null = null;
    const others: React.ReactElement[] = [];

    for (const f of fields) {
      const src = f.props?.source;
      if (!titleEl && primarySource && src === primarySource) {
        titleEl = f;
        continue;
      }
      if (!subtitleEl && secondarySource && src === secondarySource) {
        subtitleEl = f;
        continue;
      }
      others.push(f);
    }

    // Fallback: if no explicit primarySource, take the first field as title
    if (!titleEl && fields.length) {
      titleEl = fields[0];
      const idx = others.indexOf(fields[0]);
      if (idx >= 0) others.splice(idx, 1);
    }

    return { titleEl, subtitleEl, others, actions };
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <Stack spacing={1.5}>
          {[1, 2, 3].map((i) => (
            <Card key={i} sx={{ mx: 'auto', width: '100%', maxWidth: 920 }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Skeleton variant="text" width="50%" height={30} />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="rectangular" height={56} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  // Empty state
  if (!data.length) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <Card sx={{ mx: 'auto', width: '100%', maxWidth: 920 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              {translate('common.no_data_title')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 2, mx: 'auto', maxWidth: 920 }}>
      <Stack spacing={{ xs: 1, sm: 1.5 }}>
        {data.map((record: RecordType, idx) => {
          const { titleEl, subtitleEl, others, actions } = splitChildren(record);

          return (
            <ExpandableCard
              key={(record as any).id ?? idx}
              record={record}
              index={idx}
              titleEl={titleEl}
              subtitleEl={subtitleEl}
              others={others}
              actions={actions}
              detailColumns={detailColumns}
              ExpandComponent={expand}
              theme={theme}
              isXs={isXs}
              translate={translate}
            />
          );
        })}
      </Stack>
    </Box>
  );
};

export default CustomDataGridMobile;
